/**
 * Admin — Face Search event detail (/face-search/:id): Overview · Guests · Photos · Settings.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Play, Pause, RefreshCw, Trash2, Copy, QrCode, MessageCircle, ExternalLink, Download,
  AlertTriangle, Loader2, Users, Search, Images, Clock, CheckCircle2, Radio,
} from "lucide-react";
import { faceAdmin, errMsg, saveBlob } from "../lib/faceApi";
import { Btn, BrandChip, Field, Modal, ProgressBar, Stat, StatusChip, Toggle, inputCls, useToastState, usd, fmtDate, fmtDateTime } from "../lib/faceUi";

const TABS = [["overview", "Overview"], ["guests", "Guests"], ["photos", "Photos"], ["settings", "Settings"]];

function Overview({ ev, act, busy, showToast }) {
  const copy = async (text, msg) => { try { await navigator.clipboard.writeText(text); showToast(msg, "success"); } catch (_) { window.prompt("Copy", text); } };
  const qr = async () => { try { saveBlob(await faceAdmin.qr(ev.id), `${ev.title.replace(/[^a-z0-9]+/gi, "-")}-qr.png`); } catch (err) { showToast(errMsg(err), "error"); } };
  const wa = () => window.open(`https://wa.me/?text=${encodeURIComponent(`Find your photos from ${ev.couple_names || ev.title} — take a selfie here: ${ev.share_url}`)}`, "_blank", "noopener");
  const done = (ev.photos_indexed || 0) + (ev.photos_failed || 0);
  const canStart = ["listed", "paused", "error"].includes(ev.status);
  return (
    <div className="space-y-5">
      {ev.status === "listed" && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4">
          <div className="font-semibold text-amber-200">Found {ev.photos_total?.toLocaleString("en-IN")} photos{ev.photos_skipped ? ` (${ev.photos_skipped} non-JPEG/PNG files skipped)` : ""}</div>
          {ev.photos_total > 0 ? (
            <div className="mt-1 text-sm text-white/70">Indexing will cost about <b>{usd(ev.estimated_cost_usd)}</b> and take roughly <b>{ev.estimated_minutes} min</b>. Guests can start searching as soon as the first photos are indexed.</div>
          ) : ev.live_sync_minutes > 0 ? (
            <div className="mt-1 text-sm text-white/70">The folder is empty for now — that's fine in Live mode. <b>Go live</b> now, print the QR / share the link, and photos will be indexed automatically as your team uploads them (checked every {ev.live_sync_minutes} min).</div>
          ) : (
            <div className="mt-1 text-sm text-white/70">The folder has no JPEG/PNG photos yet. Add photos and click <b>Sync new photos</b>, or turn on <b>Live event mode</b> in Settings to go live now and index photos as they arrive.</div>
          )}
          <Btn className="mt-3" loading={busy === "start"} disabled={!ev.photos_total && !ev.live_sync_minutes} onClick={() => act("start")}><Play className="h-4 w-4" /> {ev.photos_total > 0 ? "Start indexing" : "Go live"}</Btn>
        </div>
      )}
      {ev.status === "error" && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-red-300" />
          <div><div className="font-semibold text-red-200">Indexing stopped with an error</div><div className="text-white/70">{ev.error}</div>
            <Btn className="mt-3" loading={busy === "start"} onClick={() => act("start")}><Play className="h-4 w-4" /> Resume</Btn></div>
        </div>
      )}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-medium">{done.toLocaleString("en-IN")} of {(ev.photos_total || 0).toLocaleString("en-IN")} photos indexed · {ev.faces_indexed || 0} faces</span>
          <span className="text-white/60">{ev.progress_pct}%{ev.photos_failed ? ` · ${ev.photos_failed} failed` : ""}</span>
        </div>
        <ProgressBar value={ev.progress_pct} />
        <div className="mt-3 flex flex-wrap gap-2">
          {ev.status === "indexing" && <Btn variant="ghost" loading={busy === "pause"} onClick={() => act("pause")}><Pause className="h-4 w-4" /> Pause</Btn>}
          {canStart && ev.status !== "listed" && <Btn loading={busy === "start"} onClick={() => act("start")}><Play className="h-4 w-4" /> Resume</Btn>}
          {ev.status !== "listing" && <Btn variant="ghost" loading={busy === "sync"} onClick={() => act("sync")}><RefreshCw className="h-4 w-4" /> Sync new photos</Btn>}
          {ev.photos_failed > 0 && <Btn variant="ghost" loading={busy === "retry"} onClick={() => act("retry")}><RefreshCw className="h-4 w-4" /> Retry {ev.photos_failed} failed</Btn>}
        </div>
        <div className="mt-3 grid gap-1 text-xs text-white/50 sm:grid-cols-3">
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Started {fmtDateTime(ev.indexing_started_at)}</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Finished {fmtDateTime(ev.indexing_finished_at)}</span>
          <span className="inline-flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Last sync {fmtDateTime(ev.last_synced_at)}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Photos" value={(ev.photos_total || 0).toLocaleString("en-IN")} sub={`${ev.faces_indexed || 0} faces`} />
        <Stat label="Guests" value={ev.stats?.guests || 0} />
        <Stat label="Searches" value={ev.stats?.searches || 0} />
        <Stat label="Downloads" value={ev.stats?.downloads || 0} />
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm font-semibold">Share with guests</div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <code className="flex-1 truncate rounded-lg bg-black/40 px-3 py-2 text-xs text-amber-200">{ev.public_url}</code>
          <Btn variant="ghost" onClick={() => copy(ev.public_url, "Guest link copied")}><Copy className="h-4 w-4" /> Copy</Btn>
          <Btn variant="ghost" onClick={qr}><QrCode className="h-4 w-4" /> QR code</Btn>
          <Btn variant="ghost" onClick={wa}><MessageCircle className="h-4 w-4" /> WhatsApp</Btn>
          <a href={ev.public_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3.5 py-2 text-sm hover:bg-white/10"><ExternalLink className="h-4 w-4" /> Preview</a>
        </div>
        <div className="mt-2 text-xs text-white/50">Print the QR at the venue, or send the WhatsApp link (it shows a preview with the cover photo). {ev.has_pin ? "Guests will need the PIN." : ""}</div>
      </div>
    </div>
  );
}

function Guests({ ev, showToast }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { faceAdmin.guests(ev.id).then(setRows).catch((e) => showToast(errMsg(e), "error")); }, [ev.id, showToast]);
  const csv = async () => { try { saveBlob(await faceAdmin.guestsCsv(ev.id), `${ev.title.replace(/[^a-z0-9]+/gi, "-")}-guests.csv`); } catch (e) { showToast(errMsg(e), "error"); } };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-white/60">{rows ? `${rows.length} guest${rows.length === 1 ? "" : "s"} · also saved in Leads & Sales` : "Loading…"}</div>
        <Btn variant="ghost" onClick={csv} disabled={!rows?.length}><Download className="h-4 w-4" /> Export CSV</Btn>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-white/50">
            <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">WhatsApp</th><th className="px-3 py-2">Matches</th><th className="px-3 py-2">Searches</th><th className="px-3 py-2">Downloads</th><th className="px-3 py-2">First seen</th></tr>
          </thead>
          <tbody>
            {(rows || []).map((g) => (
              <tr key={g.id} className="border-t border-white/5">
                <td className="px-3 py-2 font-medium">{g.name}</td>
                <td className="px-3 py-2"><a className="text-amber-300 hover:underline" href={`https://wa.me/${g.phone.replace("+", "")}`} target="_blank" rel="noopener noreferrer">{g.phone}</a></td>
                <td className="px-3 py-2">{g.last_match_count || 0}</td><td className="px-3 py-2">{g.searches || 0}</td><td className="px-3 py-2">{g.downloads || 0}</td>
                <td className="px-3 py-2 text-white/60">{fmtDateTime(g.first_seen)}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-white/50"><Users className="mx-auto mb-2 h-6 w-6" />No guests yet — share the link to get started.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Photos({ ev, showToast }) {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setData(null);
    faceAdmin.photos(ev.id, { page, status: filter, page_size: 60 }).then(setData).catch((e) => showToast(errMsg(e), "error"));
  }, [ev.id, page, filter, showToast, ev.photos_indexed]);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {[["all", "All"], ["indexed", "Indexed"], ["pending", "Pending"], ["failed", "Failed"]].map(([k, l]) => (
          <button key={k} onClick={() => { setFilter(k); setPage(1); }} className={`rounded-full px-3 py-1 text-xs ${filter === k ? "bg-amber-400 text-black" : "bg-white/5 text-white/70"}`}>{l}</button>
        ))}
        <span className="ml-auto text-xs text-white/50">{data ? `${data.total} photos` : ""}</span>
      </div>
      {!data ? <div className="flex justify-center py-10 text-white/50"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
        <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5 lg:grid-cols-8">
          {data.items.map((p) => (
            <div key={p.file_id} className="group relative aspect-square overflow-hidden rounded-lg bg-white/5" title={`${p.path ? p.path + "/" : ""}${p.name}${p.error ? "\n" + p.error : ""}`}>
              <img src={p.thumb_url.replace("sz=w1000", "sz=w400")} alt="" loading="lazy" className="h-full w-full object-cover" />
              <span className={`absolute bottom-1 right-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${p.error ? "bg-red-500 text-white" : p.indexed ? "bg-black/70 text-white" : "bg-white/30 text-white"}`}>
                {p.error ? "!" : p.indexed ? `${p.face_count}` : "…"}
              </span>
            </div>
          ))}
          {data.items.length === 0 && <div className="col-span-full py-8 text-center text-sm text-white/50">Nothing here.</div>}
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Btn variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Btn><span className="text-white/60">{page} / {pages}</span><Btn variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
      <div className="text-xs text-white/40">Badge = faces found in the photo. Faces smaller than ~2% of the frame or heavily blurred may be skipped by Rekognition.</div>
    </div>
  );
}

function Settings({ ev, onSaved, showToast }) {
  const [f, setF] = useState({ title: ev.title, couple_names: ev.couple_names || "", event_date: ev.event_date || "", pin: "", clearPin: false, allow_download: ev.allow_download, allow_browse_all: ev.allow_browse_all, require_guest_details: ev.require_guest_details, is_active: ev.is_active, expires_at: ev.expires_at ? ev.expires_at.slice(0, 10) : "", live_sync_minutes: ev.live_sync_minutes || 0 });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { title: f.title, couple_names: f.couple_names, event_date: f.event_date || null, allow_download: f.allow_download, allow_browse_all: f.allow_browse_all, require_guest_details: f.require_guest_details, is_active: f.is_active, live_sync_minutes: Number(f.live_sync_minutes) || 0 };
      if (f.clearPin) body.pin = ""; else if (f.pin) body.pin = f.pin;
      if (f.expires_at) body.expires_at = `${f.expires_at}T23:59:59+05:30`;
      onSaved(await faceAdmin.update(ev.id, body));
      showToast("Saved", "success");
    } catch (err) { showToast(errMsg(err), "error"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="max-w-xl space-y-4">
      <Field label="Event title"><input className={inputCls} value={f.title} onChange={(e) => set("title", e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Couple names"><input className={inputCls} value={f.couple_names} onChange={(e) => set("couple_names", e.target.value)} /></Field>
        <Field label="Event date"><input type="date" className={inputCls} value={f.event_date} onChange={(e) => set("event_date", e.target.value)} /></Field>
      </div>
      <Field label={ev.has_pin && !f.clearPin ? "PIN (set) — enter a new one to change" : "PIN (optional)"}>
        <div className="flex gap-2">
          <input className={inputCls} inputMode="numeric" value={f.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={ev.has_pin ? "••••" : "4–6 digits"} disabled={f.clearPin} />
          {ev.has_pin && <Btn type="button" variant="ghost" onClick={() => set("clearPin", !f.clearPin)}>{f.clearPin ? "Keep PIN" : "Remove PIN"}</Btn>}
        </div>
      </Field>
      <Field label="Link expires on" hint="Guests can't open the gallery after this date. Default is 180 days after creation.">
        <input type="date" className={inputCls} value={f.expires_at} onChange={(e) => set("expires_at", e.target.value)} />
      </Field>
      <Field label="Live event mode — auto-sync new photos from Drive" hint="During the event, new photos your team uploads to the folder get indexed automatically. Set to Off afterwards (it still syncs every 12 h).">
        <select className={inputCls} value={f.live_sync_minutes} onChange={(e) => set("live_sync_minutes", e.target.value)}>
          <option value={0}>Off (sync every 12 h / manual)</option>
          <option value={2}>Every 2 minutes</option>
          <option value={5}>Every 5 minutes (recommended for live events)</option>
          <option value={10}>Every 10 minutes</option>
          <option value={15}>Every 15 minutes</option>
          <option value={30}>Every 30 minutes</option>
        </select>
      </Field>
      <div className="space-y-2">
        <Toggle checked={f.is_active} onChange={(v) => set("is_active", v)} label="Gallery is live" hint="Turn off to hide it from guests immediately" />
        <Toggle checked={f.require_guest_details} onChange={(v) => set("require_guest_details", v)} label="Ask guests for name & WhatsApp" />
        <Toggle checked={f.allow_download} onChange={(v) => set("allow_download", v)} label="Allow full-quality downloads" />
        <Toggle checked={f.allow_browse_all} onChange={(v) => set("allow_browse_all", v)} label="Let guests browse the full gallery" />
      </div>
      <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-white/60">Drive folder: <a className="text-amber-300 hover:underline" href={ev.drive_folder_url} target="_blank" rel="noopener noreferrer">{ev.drive_folder_name || ev.drive_folder_id}</a> · created {fmtDate(ev.created_at)}</div>
      <Btn type="submit" loading={busy}>Save changes</Btn>
    </form>
  );
}

export default function FaceEventPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [toast, showToast] = useToastState();
  const [canManage, setCanManage] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try { setEv(await faceAdmin.get(id)); } catch (err) { showToast(errMsg(err), "error"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { load(); faceAdmin.config().then((c) => setCanManage(c?.can_manage !== false)).catch(() => {}); }, [load]);
  useEffect(() => {
    clearInterval(timer.current);
    if (ev && (ev.status === "indexing" || ev.status === "listing")) timer.current = setInterval(load, 4000);
    return () => clearInterval(timer.current);
  }, [ev, load]);

  const act = async (kind) => {
    setBusy(kind);
    try {
      const fn = { start: faceAdmin.start, pause: faceAdmin.pause, sync: faceAdmin.sync, retry: faceAdmin.retryFailed }[kind];
      const res = await fn(id);
      setEv(res);
      if (kind === "sync") showToast(`Sync done: ${res.sync?.new || 0} new, ${res.sync?.removed || 0} removed`, "success");
      if (kind === "start") showToast("Indexing started — guests can search as photos come in", "success");
    } catch (err) { showToast(errMsg(err), "error"); } finally { setBusy(null); }
  };
  const remove = async () => {
    setBusy("delete");
    try { await faceAdmin.remove(id); navigate("/face-search"); } catch (err) { showToast(errMsg(err), "error"); setBusy(null); }
  };

  if (!ev) return <div className="flex justify-center p-10 text-white/60"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 text-white sm:p-6">
      {toast}
      <button onClick={() => navigate("/face-search")} className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> All galleries</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-4">
          {ev.cover_url && <img src={ev.cover_url} alt="" className="h-16 w-16 rounded-xl object-cover" />}
          <div>
            <h1 className="text-2xl font-semibold">{ev.title}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-white/60"><StatusChip status={ev.status} active={ev.is_active} /><BrandChip brand={ev.brand} />{ev.live_sync_minutes > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-red-500/20 px-2.5 py-0.5 text-xs font-medium text-red-300"><Radio className="h-3 w-3" /> Live · auto-sync {ev.live_sync_minutes} min</span>}{ev.couple_names}{ev.event_date ? ` · ${fmtDate(ev.event_date)}` : ""}</div>
          </div>
        </div>
        {canManage && <Btn variant="danger" onClick={() => setDelOpen(true)}><Trash2 className="h-4 w-4" /> Delete</Btn>}
      </div>
      <div className="flex gap-1 overflow-x-auto rounded-xl bg-white/5 p-1 text-sm">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 font-medium ${tab === k ? "bg-white/15" : "text-white/60"}`}>{l}</button>)}
      </div>
      {tab === "overview" && <Overview ev={ev} act={act} busy={busy} showToast={showToast} />}
      {tab === "guests" && <Guests ev={ev} showToast={showToast} />}
      {tab === "photos" && <Photos ev={ev} showToast={showToast} />}
      {tab === "settings" && <Settings ev={ev} onSaved={setEv} showToast={showToast} />}
      <Modal open={delOpen} onClose={() => setDelOpen(false)} title="Delete this gallery?">
        <p className="text-sm text-white/70">This removes the face index, guest list and results for <b>{ev.title}</b>. Your photos on Google Drive are not touched. Type <code className="rounded bg-black/40 px-1 text-amber-200">DELETE</code> to confirm.</p>
        <input className={`${inputCls} mt-3`} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" />
        <div className="mt-4 flex justify-end gap-2"><Btn variant="ghost" onClick={() => setDelOpen(false)}>Cancel</Btn><Btn variant="danger" disabled={confirm !== "DELETE"} loading={busy === "delete"} onClick={remove}>Delete gallery</Btn></div>
      </Modal>
    </div>
  );
}
